import React, { useState, useEffect } from "react";
import { Phone, Mail, PhoneCall, ChevronUp, X } from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export default function RotatingContactButton() {
  const [open, setOpen] = useState(false);
  const [currentIcon, setCurrentIcon] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const floatingIcons = [
    <Phone size={22} key="phone" />,
    <Mail size={22} key="mail" />,
    <PhoneCall size={22} key="callback" />,
  ];

  useEffect(() => {
    if (!open) {
      const interval = setInterval(() => {
        setCurrentIcon((prev) => (prev + 1) % floatingIcons.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [open]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const resetForm = () => {
    setForm({ name: "", phone: "" }); // reset with +91 prefilled
    setErrors({});
    setSubmitted(false);
  };
  const validateForm = () => {
    let newErrors = {};
    if (!form.name.trim()) newErrors.name = "Please enter your Full Name";

    // Only check for 10 digits, since we prepend +91 in UI
    if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        setLoading(true); // start loading
        await addDoc(collection(db, "callbackRequests"), {
          name: form.name,
          phone: "+91" + form.phone, // store with country code
          createdAt: serverTimestamp(),
          status: "Pending",         // default
  userResponse: "",          // default empty

        });
        setSubmitted(true);
        setLoading(false); // stop loading

        // Keep success message for 30 seconds
        setTimeout(() => {
          setSubmitted(false);
          setShowPopup(false);
          setForm({ name: "", phone: "+91" });
        }, 30000);
      } catch (err) {
        console.error("Error saving callback request:", err);
        alert("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className="fixed bottom-8 right-8 z-50 flex flex-col items-end gap-4">
      {/* Contact Panel */}
      {open && (
        <div className="mb-4 bg-white shadow-2xl rounded-xl p-4 w-64">
          <div className="flex flex-col gap-4">
            {/* Direct Call */}
            <a
              href="tel:+919281010745"
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white">
                <Phone size={20} />
              </div>
              <span className="text-gray-700">Direct Call</span>
            </a>

            {/* WhatsApp */}
            <a
              href="https://wa.me/+919281010745"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white">
                <svg
                  role="img"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 fill-current"
                >
                  <title>WhatsApp</title>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </div>
              <span className="text-gray-700">WhatsApp</span>
            </a>

            {/* Email */}
            <a
              href="mailto:contact@jastiphysiochiro.com"
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-red-400 flex items-center justify-center text-white">
                <Mail size={20} />
              </div>
              <span className="text-gray-700">Email Us</span>
            </a>

            {/* Callback Request (Popup) */}
            <button
              onClick={() => setShowPopup(true)}
              className="flex items-center gap-3 hover:bg-gray-100 p-2 rounded-lg transition"
            >
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white">
                <PhoneCall size={20} />
              </div>
              <span className="text-gray-700">Callback Request</span>
            </button>
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {showPopup && (
        <div
          className="fixed inset-0 flex items-center justify-center 
                bg-black bg-opacity-50 backdrop-blur-sm z-[999]"
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 
                  w-[90%] max-w-xs sm:max-w-md lg:max-w-lg 
                  w-[85%] max-w-sm sm:min-h-[260px] relative flex flex-col justify-center"
          >
            <button
              onClick={() => {
                resetForm();
                setShowPopup(false);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <X size={20} />
            </button>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <p className="text-gray-600 text-sm">
                  Enter your details and our team will reach out shortly.
                </p>

                {/* Name */}
                <div>
                  <input
                    type="text"
                    placeholder="Enter Your Name"
                    className="w-full border p-2 rounded-lg"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Phone */}
                {/* Phone */}
                {/* Phone */}
                <div>
                  <div
                    className={`flex items-center border rounded-lg ${
                      errors.phone ? "border-red-500" : ""
                    }`}
                  >
                    <span className="px-3 text-gray-600 select-none">+91</span>
                    <input
                      type="text"
                      className="w-full p-2 rounded-r-lg outline-none"
                      placeholder="Enter your number"
                      value={form.phone}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          phone: e.target.value.replace(/\D/g, ""), // only digits
                        })
                      }
                      maxLength={10}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-[#204080] text-white py-2 rounded-lg transition ${
                    loading
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:bg-[#2E5AAC]"
                  }`}
                >
                  {loading ? "Submitting..." : "Request Callback"}
                </button>
              </form>
            ) : (
              <div className="text-center p-4">
                <h3 className="text-green-600 font-semibold">
                  ✅ Request Sent!
                </h3>
                <p className="text-gray-600 text-md mt-2">
                  Thank you {form.name}, we will contact you at {form.phone}{" "}
                  shortly.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rotating Contact Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-12 h-12 rounded-full bg-gradient-to-r from-[#204080] to-[#2E5AAC] 
                   text-white flex items-center justify-center shadow-xl 
                   hover:scale-110 transition-transform duration-300"
        title="Contact"
      >
        {open ? "✖" : floatingIcons[currentIcon]}
      </button>

      {/* Scroll-to-Top Button */}
      <button
        onClick={scrollToTop}
        className="w-12 h-12 rounded-full bg-gradient-to-r from-[#204080] to-[#2E5AAC] 
                   text-white flex items-center justify-center shadow-xl 
                   hover:scale-110 transition-transform duration-300"
        title="Scroll to Top"
      >
        <ChevronUp size={22} />
      </button>

      {/* Logo */}
      <a
        href="https://www.charvionglobal.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="relative group flex flex-col items-end"
      >
        <div className="p-[2px] rounded-full bg-[conic-gradient(#09558c_0deg_180deg,#f71014_180deg_360deg)]">
          <div className="w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-white">
            <img
              src="/cgs.png"
              alt="Company Logo"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <span className="mt-2 text-sm font-bold text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity">
          Powered By Charvion Global Pvt. Ltd.
        </span>
      </a>
    </div>
  );
}
